import { CriteriaOperatorEnum } from '../enums/criteriaOperator.enum';
import {
  CATALOG_CREATION_CRITERIA_AVAILABILITIES,
  CATALOG_CREATION_CRITERIA_CONFIGS,
  CATALOG_CREATION_CRITERIA_PHRASE_MAX_LABELS,
} from './catalogCreationCriteria.consts';
import {
  BuildCatalogCreationCriterionChipsParams,
  BuildCatalogCreationCriterionPhraseParams,
  CatalogCreationCriterionChip,
  CatalogCreationCriterionEnum,
  CatalogCreationCriterionLabeledValue,
  FilterCatalogCreationCriterionOptionsParams,
  JoinCatalogCreationCriterionLabelGroupsParams,
  MergeSelectedCatalogCreationCriterionOptionsParams,
  ToggleCatalogCreationCriterionValueParams,
} from './catalogCreationCriteria.types';

export const findCatalogCreationCriterionConfig = (criterion: CatalogCreationCriterionEnum) => {
  const config = CATALOG_CREATION_CRITERIA_CONFIGS.find(
    (candidate) => candidate.criterion === criterion,
  );

  if (!config) {
    throw new Error(`Unknown catalog creation criterion: ${criterion}`);
  }

  return config;
};

export const buildCatalogCreationCriterionChips = ({
  values,
  operators,
}: BuildCatalogCreationCriterionChipsParams): CatalogCreationCriterionChip[] =>
  CATALOG_CREATION_CRITERIA_CONFIGS.filter(({ valuesField }) => values[valuesField].length > 0).map(
    ({ criterion, phraseLabel, valuesField, operatorField }) => ({
      criterion,
      phraseLabel,
      values: values[valuesField],
      operator: operatorField ? operators[operatorField] : CriteriaOperatorEnum.OR,
    }),
  );

export const groupCatalogCreationCriterionLabels = (
  labeledValues: CatalogCreationCriterionLabeledValue[],
) => {
  const labelsByGroupKey = new Map<string, string[]>();

  labeledValues.forEach(({ label, groupKey }) => {
    const groupLabels = labelsByGroupKey.get(groupKey) ?? [];

    groupLabels.push(label);
    labelsByGroupKey.set(groupKey, groupLabels);
  });

  return [...labelsByGroupKey.values()];
};

export const joinCatalogCreationCriterionLabelGroups = ({
  labelGroups,
  innerOperatorLabel,
  outerOperatorLabel,
}: JoinCatalogCreationCriterionLabelGroupsParams) => {
  const hasSeveralGroups = labelGroups.length > 1;

  return labelGroups
    .map((groupLabels) => {
      const joinedLabels = groupLabels.join(` ${innerOperatorLabel} `);

      if (hasSeveralGroups && groupLabels.length > 1) {
        return `(${joinedLabels})`;
      }

      return joinedLabels;
    })
    .join(` ${outerOperatorLabel} `);
};

export const buildCatalogCreationCriterionPhrase = ({
  labelGroups,
  innerOperatorLabel,
  outerOperatorLabel,
  remainingLabel,
}: BuildCatalogCreationCriterionPhraseParams) => {
  const valueLabels = labelGroups.flat();

  if (valueLabels.length <= CATALOG_CREATION_CRITERIA_PHRASE_MAX_LABELS) {
    return joinCatalogCreationCriterionLabelGroups({
      labelGroups,
      innerOperatorLabel,
      outerOperatorLabel,
    });
  }

  const [firstGroupLabels] = labelGroups;
  const firstJoinLabel = firstGroupLabels.length > 1 ? innerOperatorLabel : outerOperatorLabel;

  return `${valueLabels[0]} ${firstJoinLabel} ${remainingLabel}`;
};

export const toggleCatalogCreationCriterionValue = ({
  values,
  optionId,
}: ToggleCatalogCreationCriterionValueParams) => {
  if (values.includes(optionId)) {
    return values.filter((value) => value !== optionId);
  }

  return [...values, optionId];
};

export const mergeSelectedCatalogCreationCriterionOptions = ({
  options,
  selectedIds,
  optionLabelsById,
}: MergeSelectedCatalogCreationCriterionOptionsParams) => {
  const optionsById = new Map(options.map((option) => [option.id, option]));

  const selectedOptions = selectedIds.map((selectedId) => {
    const fetchedOption = optionsById.get(selectedId);

    if (fetchedOption) {
      return fetchedOption;
    }

    const optionLabel = optionLabelsById[selectedId];

    return {
      id: selectedId,
      name: optionLabel?.name ?? selectedId,
      description: optionLabel?.description,
      groupId: optionLabel?.groupId,
    };
  });

  return [...selectedOptions, ...options.filter(({ id }) => !selectedIds.includes(id))];
};

export const filterCatalogCreationCriterionOptions = ({
  options,
  search,
}: FilterCatalogCreationCriterionOptionsParams) => {
  const normalizedSearch = search.trim().toLowerCase();

  if (!normalizedSearch) {
    return options;
  }

  return options.filter(({ name }) => name.toLowerCase().includes(normalizedSearch));
};

export const toCatalogCriterionAvailabilities = (values: string[]) =>
  CATALOG_CREATION_CRITERIA_AVAILABILITIES.filter((availability) => values.includes(availability));
